import { MAX_BLUEPRINT_FILE_SIZE } from '@/common/utils/blueprintFilePolicy';
import type { BlueprintDetection } from './blueprintDetectionTypes';

export function detectBlueprintFile(
  file: File,
  signal: AbortSignal
): Promise<BlueprintDetection> {
  return new Promise((resolve, reject) => {
    if (signal.aborted)
      return reject(new DOMException('Cancelled', 'AbortError'));
    if (file.size > MAX_BLUEPRINT_FILE_SIZE || !file.size)
      return reject(new Error('Preset exceeds the inspection limit.'));
    const worker = new Worker(
      new URL('./blueprintDetection.worker.ts', import.meta.url),
      { type: 'module' }
    );
    let settled = false;
    const finish = (result?: BlueprintDetection, error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      signal.removeEventListener('abort', abort);
      worker.terminate();
      if (result) resolve(result);
      else reject(error || new Error('Could not inspect this preset.'));
    };
    const abort = () =>
      finish(undefined, new DOMException('Cancelled', 'AbortError'));
    const timeout = setTimeout(
      () =>
        finish(
          undefined,
          new Error('Inspection took too long. Enter the requirements below.')
        ),
      5000
    );
    signal.addEventListener('abort', abort, { once: true });
    worker.onmessage = (
      event: MessageEvent<{ result?: BlueprintDetection; error?: string }>
    ) =>
      finish(
        event.data.result,
        event.data.error ? new Error(event.data.error) : undefined
      );
    worker.onerror = () => finish();
    worker.onmessageerror = () => finish();
    try {
      worker.postMessage(file);
    } catch {
      finish();
    }
  });
}
